/* @flow */

import {
  AUS_STATES,
  USA_STATES,
  CANADA_PROVINCES,
  MEX_STATES
} from "./fields/regions";
import { SUPPORTED_EU_COUNTRIES_METADATA } from "./countryHelper";

const FULFILLMENT_PRODUCTS_DELIVERED_LABEL = "When are products delivered?";
const BRAINTREE_LEGAL_URL = "https://www.braintreepayments.com/legal";
const UK_LOA_URL =
  "https://www.paypalobjects.com/policydashboard/assets/regions/lux/en/loa-uk.pdf";
const EU_LOA_URL =
  "https://www.paypalobjects.com/digitalassets/c/compliance/eu/gb/en/loa.pdf";
const IDENTITY_VERIFICATION_TITLE = "Identity Verification";
const IDENTITY_VERIFICATION_ID_AND_ADDRESS_DESCRIPTION =
  "To verify your identity, we require a copy of any valid government-issued national ID or passport. We also require proof of residential address, such as a utility bill dated within the past 6 months.";
const DATE_PLACEHOLDER_MDY = "MM/DD/YYYY";
const PHONE_PLACEHOLDER_APAC = "00 0000 0000";
const INDIVIDUAL_ACCOUNT_NUMBER_LABEL = "Individual Account Number";
const ACCOUNT_NUMBER_LABEL = "Account Number";
const ACCOUNT_NUMBER_LAST_DIGITS_DESCRIPTION =
  "Last 9-12 digits of your full account number";
const ROUTING_NUMBER_FIRST_DIGITS_DESCRIPTION =
  "First 6 digits of your full account number";
const APAC_BANK_STATEMENT_DOCUMENT_DESCRIPTION =
  "Upload a bank statement from the last 3 months. It must show your bank's logo, statement date, legal business name, and account number. " +
  "This helps us verify that the bank account provided is valid, active, and associated with your business, and confirms that we route settlements to the correct account. " +
  "Accepted file formats: PDF, JPG, or PNG. File size is limited to 10 MB.";
const BUSINESS_REGISTRATION_NUMBER_LABEL = "Business registration number";
const businessRegistrationDocumentDescription = (documentName, issuer) =>
  `Upload a copy of your ${documentName}, issued by ${issuer}. ` +
  "It must show your business's legal name matching your application, your business registration number, and director and shareholder information. " +
  "Accepted file formats: PDF, JPG, or PNG. File size is limited to 10 MB.";
const BIC_SWIFT_CODE_LABEL = "BIC (SWIFT) code";
const PHONE_PLACEHOLDER_NA = "(000) 000-0000";
const BENEFICIAL_OWNERS_AND_MANAGERS_TITLE =
  "Beneficial Owners and Business Managers";
const ADD_OWNER_OR_MANAGER_TEXT = "Add Owner or Manager";
const ROUTING_NUMBER_LABEL = "Routing Number";

const config = document.querySelector("#formConfig") || {};
let formConfig = {};
if (config.textContent) {
  formConfig = JSON.parse(config.textContent);
}

const localRegionArray = regions => {
  return Object.entries(regions).map(([key, value]) => ({
    key,
    text: value
  }));
};

const local_regions = localRegionArray(formConfig.regions || {});

const euCountryToIdLabels = {
  AND: {
    legalUrl: "https://www.braintreepayments.com/ad/legal"
  },
  AUT: {
    legalUrl: "https://www.braintreepayments.com/at/legal"
  },
  BEL: {
    legalUrl: "https://www.paypal.com/be/legalhub/braintree"
  },
  BGR: {
    currencySymbol: "лв",
    recommendedProcessingVolume: "60000 лв.",
    legalUrl: "https://www.paypal.com/bg/legalhub/braintree"
  },
  CHE: {
    currencySymbol: "CHF",
    recommendedProcessingVolume: "CHF 60’000",
    legalUrl: "https://www.paypal.com/ch/legalhub/braintree"
  },
  CYP: {
    legalUrl: "https://www.braintreepayments.com/cy/legal"
  },
  CZE: {
    currencySymbol: "Kč",
    recommendedProcessingVolume: "60 000 Kč",
    legalUrl: "https://www.paypal.com/cz/legalhub/braintree"
  },
  DEU: {
    legalUrl: "https://www.braintreepayments.com/de/legal"
  },
  DNK: {
    currencySymbol: "kr",
    recommendedProcessingVolume: "60.000 kr.",
    idNumberLabel: "Central Person Register (CPR)",
    idNumberPlaceholder: "999999-9999",
    legalUrl: "https://www.paypal.com/dk/legalhub/braintree"
  },
  ESP: {
    legalUrl: "https://www.paypal.com/es/legalhub/braintree"
  },
  EST: {
    legalUrl: "https://www.paypal.com/ee/legalhub/braintree"
  },
  FIN: {
    legalUrl: "https://www.paypal.com/fi/legalhub/braintree"
  },
  FRA: {
    legalUrl: "https://www.braintreepayments.com/fr/legal"
  },
  GRC: {
    legalUrl: "https://www.paypal.com/gr/legalhub/braintree"
  },
  GGY: {
    currencySymbol: "£",
    recommendedProcessingVolume: "£60,000",
    legalUrl: "https://www.braintreepayments.com/gg/legal"
  },
  GBR: {
    currencySymbol: "£",
    recommendedProcessingVolume: "£60,000",
    legalUrl: "https://www.paypal.com/gb/legalhub/braintree",
    loaUrl: UK_LOA_URL
  },
  GIB: {
    currencyISOCode: "EUR",
    currencySymbol: "€",
    recommendedProcessingVolume: "€60,000",
    legalUrl: "https://www.paypal.com/gi/legalhub/braintree"
  },
  HUN: {
    currencySymbol: "Ft",
    recommendedProcessingVolume: "60 000 Ft",
    legalUrl: "https://www.paypal.com/hu/legalhub/braintree"
  },
  HRV: {
    currencySymbol: "kn",
    recommendedProcessingVolume: "60.000 kn",
    legalUrl: "https://www.paypal.com/hr/legalhub/braintree"
  },
  ISL: {
    currencySymbol: "kr",
    recommendedProcessingVolume: "60,000 kr",
    legalUrl: "https://www.paypal.com/is/legalhub/braintree"
  },
  IRL: {
    legalUrl: "https://www.paypal.com/ie/legalhub/braintree"
  },
  IMN: {
    currencySymbol: "£",
    recommendedProcessingVolume: "£60,000",
    legalUrl: "https://www.paypal.com/im/legalhub/braintree"
  },
  ITA: {
    idNumberLabel: "Codice Fiscale",
    idNumberPlaceholder: "AAA AAA 99A99 ****A",
    legalUrl: "https://www.braintreepayments.com/it/legal"
  },
  JEY: {
    currencySymbol: "£",
    recommendedProcessingVolume: "£60,000",
    legalUrl: "https://www.paypal.com/je/legalhub/braintree"
  },
  LVA: {
    legalUrl: "https://www.paypal.com/lv/legalhub/braintree"
  },
  LIE: {
    currencySymbol: "CHF",
    recommendedProcessingVolume: "60’000",
    legalUrl: "https://www.paypal.com/li/legalhub/braintree"
  },
  LTU: {
    legalUrl: "https://www.paypal.com/lt/legalhub/braintree"
  },
  LUX: {
    legalUrl: "https://www.paypal.com/lu/legalhub/braintree"
  },
  MLT: {
    legalUrl: "https://www.paypal.com/mt/legalhub/braintree"
  },
  MCO: {
    legalUrl: "https://www.paypal.com/mc/legalhub/braintree"
  },
  NLD: {
    idNumberLabel: "BSN",
    idNumberPlaceholder: "99999999(?9)", // NLD can have 8-9 digits
    legalUrl: "https://www.paypal.com/nl/legalhub/braintree"
  },
  NOR: {
    currencySymbol: "kr",
    recommendedProcessingVolume: "60,000 kr",
    idNumberLabel: "Birth Number (optional)",
    idNumberPlaceholder: undefined,
    legalUrl: "https://www.paypal.com/no/legalhub/braintree"
  },
  POL: {
    currencySymbol: "zł",
    recommendedProcessingVolume: "60 000 zł",
    legalUrl: "https://www.paypal.com/pl/legalhub/braintree"
  },
  PRT: {
    idNumberLabel: "NIF (optional)",
    idNumberPlaceholder: undefined,
    legalUrl: "https://www.paypal.com/pt/legalhub/braintree"
  },
  ROU: {
    legalUrl: "https://www.paypal.com/ro/legalhub/braintree"
  },
  SMR: {
    legalUrl: "https://www.braintreepayments.com/sm/legal"
  },
  SVK: {
    idNumberLabel: "Birth Number (optional)",
    idNumberPlaceholder: undefined,
    legalUrl: "https://www.paypal.com/sk/legalhub/braintree"
  },
  SVN: {
    legalUrl: "https://www.paypal.com/si/legalhub/braintree"
  },
  SWE: {
    currencySymbol: "kr",
    recommendedProcessingVolume: "60 000 kr",
    idNumberLabel: "Swedish Personal Identification Number",
    idNumberPlaceholder: "AAA AAA 99A99 ****A",
    legalUrl: "https://www.paypal.com/se/legalhub/braintree"
  }
};
const euCountryConfigs = SUPPORTED_EU_COUNTRIES_METADATA.reduce(
  (euConfig, { name, countryCode, currencyISOCode }) => {
    const defaultsForCountry = euCountryToIdLabels[countryCode] || {};
    euConfig[countryCode] = {
      name,
      currencyISOCode: defaultsForCountry.currencyISOCode || currencyISOCode,
      isEU: true,
      currencySymbol: defaultsForCountry.currencySymbol || "€",
      recommendedProcessingVolume:
        defaultsForCountry.recommendedProcessingVolume || "60.000 €",
      fillBicAndIbanFields: true,
      businessTaxIdLabel: "Registration Number",
      businessTaxIdFieldName: "registration_number",
      localityLabel: "City/Town",
      regionLabel: "Province",
      datePlaceholder: "YYYY-MM-DD",
      phonePlaceholder: "+44 1234567890",
      fulfillmentCompletedInLabel: FULFILLMENT_PRODUCTS_DELIVERED_LABEL,
      workflow: "eu",
      legalUrl: defaultsForCountry.legalUrl || BRAINTREE_LEGAL_URL,
      loaUrl: defaultsForCountry.loaUrl || EU_LOA_URL,
      maxContacts: 6,
      contacts: {
        beneficialOwnerSection: {
          title: "Beneficial Owners",
          addButtonText: "Add Owner"
        },
        documentManager: {
          title: IDENTITY_VERIFICATION_TITLE,
          description: IDENTITY_VERIFICATION_ID_AND_ADDRESS_DESCRIPTION
        }
      },
      accountNumberLabel: "IBAN",
      accountNumberDescription:
        "Your IBAN (International Bank Account Number) is used by banks in European countries for international fund transfers.",
      accountNumberPlaceholder: "",
      routingNumberLabel: "BIC",
      routingNumberDescription:
        "A BIC (Bank Identifier Code) is a unique identifier for a bank.",
      routingNumberPlaceholder: "",
      idNumberLabel: defaultsForCountry.idNumberLabel || "ID Num",
      idNumberPlaceholder:
        defaultsForCountry.idNumberPlaceholder || "I are placeholder",
      regions: local_regions,
      renderRegionDropDown: false,
      renderInPerson: false
    };
    return euConfig;
  },
  {}
);

// The UK's identity-verification copy diverges from the rest of the EU: it keeps the
// Sole-Proprietor-only proof-of-address requirement, with a shorter 3-month document window.
euCountryConfigs.GBR.contacts.documentManager.description =
  "To verify your identity, we require a copy of any valid government-issued national ID or passport. If you are a Sole Proprietor, we will also need proof of residential address, such as a utility bill dated within the past 3 months.";

const APAC_INDIVIDUAL_ACCOUNT_COUNTRY_METADATA = {
  MYS: {
    name: "Malaysia",
    currencyISOCode: "MYR",
    currencySymbol: "RMR",
    recommendedProcessingVolume: "RMR245,000 MYR",
    regionLabel: "Region",
    legalUrl: "https://www.paypal.com/my/legalhub/braintree",
    loaUrl:
      "https://www.paypalobjects.com/policydashboard/assets/regions/my/en/loa.pdf",
    businessRegistrationDocumentName: "Certificate of Registration",
    businessRegistrationDocumentIssuer:
      "the Companies Commission of Malaysia (SSM)"
  },
  HKG: {
    name: "Hong Kong",
    currencyISOCode: "HKD",
    currencySymbol: "$",
    recommendedProcessingVolume: "$470,000 HKD",
    regionLabel: "District",
    legalUrl: "https://www.paypal.com/hk/legalhub/braintree",
    loaUrl:
      "https://www.paypalobjects.com/policydashboard/assets/regions/hk/en/loa.pdf",
    businessRegistrationDocumentName: "NC1 Incorporation Form",
    businessRegistrationDocumentIssuer: "the Companies Registry"
  },
  SGP: {
    name: "Singapore",
    currencyISOCode: "SGD",
    currencySymbol: "$",
    recommendedProcessingVolume: "$80,000 SGD",
    regionLabel: "Region",
    legalUrl: "https://www.paypal.com/sg/legalhub/braintree",
    loaUrl:
      "https://www.paypalobjects.com/policydashboard/assets/regions/sg/en/loa.pdf",
    businessRegistrationDocumentName: "BizFile",
    businessRegistrationDocumentIssuer:
      "the Accounting and Corporate Regulatory Authority (ACRA)"
  }
};

const apacIndividualAccountCountryConfigs = Object.keys(
  APAC_INDIVIDUAL_ACCOUNT_COUNTRY_METADATA
).reduce((configs, countryCode) => {
  const {
    name,
    currencyISOCode,
    currencySymbol,
    recommendedProcessingVolume,
    regionLabel,
    legalUrl,
    loaUrl,
    businessRegistrationDocumentName,
    businessRegistrationDocumentIssuer
  } = APAC_INDIVIDUAL_ACCOUNT_COUNTRY_METADATA[countryCode];
  configs[countryCode] = {
    businessTaxIdLabel: BUSINESS_REGISTRATION_NUMBER_LABEL,
    currencyISOCode,
    currencySymbol,
    recommendedProcessingVolume,
    fulfillmentCompletedInLabel: FULFILLMENT_PRODUCTS_DELIVERED_LABEL,
    localityLabel: "Suburb",
    regionLabel,
    maxContacts: 5,
    name,
    phonePlaceholder: PHONE_PLACEHOLDER_APAC,
    postalCodePlaceholder: "0000",
    renderRefundPolicy: false,
    renderShipPhysicalGoods: false,
    renderIdNumber: true,
    idNumberLabel: "ID Number",
    renderInPerson: false,
    workflow: "apac",
    documentDescription: APAC_BANK_STATEMENT_DOCUMENT_DESCRIPTION,
    businessRegistrationDocumentDescription: businessRegistrationDocumentDescription(
      businessRegistrationDocumentName,
      businessRegistrationDocumentIssuer
    ),
    contacts: {
      documentManager: {
        title: IDENTITY_VERIFICATION_TITLE,
        description: IDENTITY_VERIFICATION_ID_AND_ADDRESS_DESCRIPTION
      }
    },
    fillBicAndIbanFields: false,
    accountNumberLabel: INDIVIDUAL_ACCOUNT_NUMBER_LABEL,
    accountNumberDescription: ACCOUNT_NUMBER_LAST_DIGITS_DESCRIPTION,
    accountNumberPlaceholder: "000000000000",
    routingNumberLabel: BIC_SWIFT_CODE_LABEL,
    routingNumberDescription: ROUTING_NUMBER_FIRST_DIGITS_DESCRIPTION,
    routingNumberPlaceholder: "000000",
    legalUrl,
    loaUrl,
    regions: [],
    renderRegionDropDown: false
  };
  return configs;
}, {});

const apacCountryConfigs = {
  AUS: {
    datePlaceholder: DATE_PLACEHOLDER_MDY,
    businessTaxIdLabel: "ABN or ACN",
    businessAddressStreetLabel:
      "Must be the address that your business registered with ASIC. We will not accept a PO Box.",
    currencyISOCode: "AUD",
    currencySymbol: "$",
    recommendedProcessingVolume: "$60,000 AUD",
    fulfillmentCompletedInLabel: FULFILLMENT_PRODUCTS_DELIVERED_LABEL,
    localityLabel: "Suburb",
    regionLabel: "State",
    maxContacts: 5,
    name: "Australia",
    phonePlaceholder: PHONE_PLACEHOLDER_APAC,
    postalCodePlaceholder: "0000",
    documentDescription:
      "Upload a bank statement from the last 6 months for your disbursement account and direct debit account, if different. It must show your bank's logo, statement date, legal business name, and account number. This helps us verify that the bank account provided is valid, active, and associated with your business, and confirms that we route settlements and direct debits to the correct account. Accepted file formats: PDF, JPG, or PNG. File size is limited to 10 MB.",
    renderRefundPolicy: false,
    renderShipPhysicalGoods: false,
    renderIdNumber: false,
    renderInPerson: false,
    workflow: "apac",
    contacts: {
      documentManager: {
        title: IDENTITY_VERIFICATION_TITLE,
        description:
          "To verify your identity, we request a copy of any valid government-issued photo identification. We also require proof of residential address, such as a utility bill dated within the past 3 months."
      }
    },
    fillBicAndIbanFields: false,
    accountNumberLabel: ACCOUNT_NUMBER_LABEL,
    routingNumberLabel: "BSB Number",
    routingNumberPlaceholder: "00000",
    legalUrl: "https://www.braintreepayments.com/au/legal",
    loaUrl:
      "https://www.paypalobjects.com/policydashboard/assets/regions/au/en/loa.pdf",
    regions: AUS_STATES,
    renderRegionDropDown: true
  },
  NZL: {
    businessTaxIdLabel: "NZBN",
    currencyISOCode: "NZD",
    currencySymbol: "$",
    recommendedProcessingVolume: "$60,000 NZD",
    fulfillmentCompletedInLabel: FULFILLMENT_PRODUCTS_DELIVERED_LABEL,
    localityLabel: "Suburb",
    regionLabel: "City/Town",
    maxContacts: 5,
    name: "New Zealand",
    phonePlaceholder: PHONE_PLACEHOLDER_APAC,
    postalCodePlaceholder: "0000",
    renderRefundPolicy: false,
    renderShipPhysicalGoods: false,
    renderIdNumber: false,
    renderInPerson: false,
    workflow: "apac",
    contacts: {
      beneficialOwnerSection: {
        title: "Beneficial Owners",
        addButtonText: "Add Owner"
      },
      documentManager: {
        title: IDENTITY_VERIFICATION_TITLE,
        description:
          "To verify this individual's identity, we require a valid copy of a passport and proof of residential address, such as a utility bill dated within the past 12 months. If a passport is unavailable, we will accept a valid New Zealand Driver's License accompanied by another item issued by a New Zealand Government agency that contains the individual's name and signature. A notary or other certified signatory must certify all requested documentation."
      }
    },
    fillBicAndIbanFields: false,
    accountNumberLabel: INDIVIDUAL_ACCOUNT_NUMBER_LABEL,
    accountNumberDescription: ACCOUNT_NUMBER_LAST_DIGITS_DESCRIPTION,
    accountNumberPlaceholder: "000000000000",
    routingNumberLabel: "Bank Code",
    routingNumberDescription: ROUTING_NUMBER_FIRST_DIGITS_DESCRIPTION,
    routingNumberPlaceholder: "000000",
    legalUrl: "https://www.paypal.com/nz/legalhub/braintree",
    regions: [],
    renderRegionDropDown: false
  },
  ...apacIndividualAccountCountryConfigs
};
const northAmericaCountryConfigs = {
  USA: {
    datePlaceholder: DATE_PLACEHOLDER_MDY,
    businessTaxIdLabel: "Tax ID",
    currencyISOCode: "USD",
    currencySymbol: "$",
    recommendedProcessingVolume: "$60,000 USD",
    fulfillmentCompletedInLabel: "One-Time Purchase Delivery",
    localityLabel: "City",
    regionLabel: "State",
    maxContacts: 6,
    name: "United States",
    phonePlaceholder: PHONE_PLACEHOLDER_NA,
    postalCodePlaceholder: undefined,
    renderRefundPolicy: true,
    renderShipPhysicalGoods: true,
    renderIdNumber: true,
    renderInPerson: true,
    idNumberLabel: "ID Number",
    idNumberPlaceholder: "123-45-6789",
    workflow: "us_direct",
    contacts: {
      beneficialOwnerSection: {
        title: BENEFICIAL_OWNERS_AND_MANAGERS_TITLE,
        addButtonText: ADD_OWNER_OR_MANAGER_TEXT
      },
      documentManager: {
        title: IDENTITY_VERIFICATION_TITLE,
        description:
          "To verify your identity, we require a copy of any valid government-issued photo identification and proof of residential address, such as a utility bill dated within the past 12 months."
      }
    },
    fillBicAndIbanFields: false,
    accountNumberLabel: ACCOUNT_NUMBER_LABEL,
    routingNumberLabel: ROUTING_NUMBER_LABEL,
    routingNumberPlaceholder: "00000",
    legalUrl: BRAINTREE_LEGAL_URL,
    regions: USA_STATES,
    renderRegionDropDown: true
  },
  CAN: {
    datePlaceholder: DATE_PLACEHOLDER_MDY,
    businessTaxIdLabel: "Provincial Business Registration Number",
    currencyISOCode: "CAD",
    currencySymbol: "$",
    recommendedProcessingVolume: "$60,000 CAD",
    fulfillmentCompletedInLabel: FULFILLMENT_PRODUCTS_DELIVERED_LABEL,
    name: "Canada",
    localityLabel: "City",
    maxContacts: 6,
    regionLabel: "Province",
    phonePlaceholder: PHONE_PLACEHOLDER_NA,
    postalCodePlaceholder: undefined,
    renderRefundPolicy: true,
    renderShipPhysicalGoods: true,
    renderIdNumber: false,
    renderInPerson: false,
    idNumberLabel: "Social Security Number",
    idNumberPlaceholder: "123-45-6789",
    contacts: {
      beneficialOwnerSection: {
        title: BENEFICIAL_OWNERS_AND_MANAGERS_TITLE,
        addButtonText: ADD_OWNER_OR_MANAGER_TEXT
      },
      documentManager: {
        title: IDENTITY_VERIFICATION_TITLE,
        description:
          "To verify your identity, we require a copy of drivers license (front only) or first 2 pages of passport"
      }
    },
    fillBicAndIbanFields: false,
    accountNumberLabel: ACCOUNT_NUMBER_LABEL,
    accountNumberPlaceholder: "000000000000",
    accountNumberDescription: "",
    routingNumberLabel: ROUTING_NUMBER_LABEL,
    routingNumberPlaceholder: "00000",
    routingNumberDescription: "",
    renderRegionDropDown: true,
    legalUrl: "https://www.braintreepayments.com/ca/legal",
    regions: CANADA_PROVINCES,
    workflow: "canada"
  },
  MEX: {
    regions: MEX_STATES,
    regionLabel: "States"
  }
};

const localAcquiringCountryConfigs = {
  BRA: {
    datePlaceholder: DATE_PLACEHOLDER_MDY,
    businessTaxIdLabel: "Tax ID",
    currencyISOCode: "BRL",
    currencySymbol: "R$",
    recommendedProcessingVolume: "0 BRL",
    fulfillmentCompletedInLabel: "One-Time Purchase Delivery",
    localityLabel: "City",
    regionLabel: "State",
    maxContacts: 6,
    name: "Brazil",
    phonePlaceholder: PHONE_PLACEHOLDER_NA,
    postalCodePlaceholder: undefined,
    renderRefundPolicy: false,
    renderShipPhysicalGoods: false,
    renderIdNumber: false,
    renderInPerson: false,
    workflow: "local_acquiring",
    contacts: {
      beneficialOwnerSection: {
        title: BENEFICIAL_OWNERS_AND_MANAGERS_TITLE,
        addButtonText: ADD_OWNER_OR_MANAGER_TEXT
      },
      documentManager: {
        title: IDENTITY_VERIFICATION_TITLE,
        description:
          "To verify your identity, we require a copy of any valid government-issued photo identification and proof of residential address, such as a utility bill dated within the past 12 months."
      }
    },
    fillBicAndIbanFields: false,
    accountNumberLabel: ACCOUNT_NUMBER_LABEL,
    routingNumberLabel: ROUTING_NUMBER_LABEL,
    routingNumberPlaceholder: "00000",
    legalUrl: BRAINTREE_LEGAL_URL,
    regions: [],
    renderRegionDropDown: false
  }
};
const countryConfig = {
  ...euCountryConfigs,
  ...apacCountryConfigs,
  ...northAmericaCountryConfigs,
  ...localAcquiringCountryConfigs
};

export default countryConfig;
